import { createApi } from '@reduxjs/toolkit/query/react';

import { OrderType } from '@/api/order/models/OrderType';

import { axiosBaseQuery } from '../axios/axiosBaseQuery';
import { PendingOrdersReply } from './dtos/PendingOrdersReply';
import { PostDepositOrderDto } from './dtos/PostDepositOrderDto';
import { PostOrderDto } from './dtos/PostOrderDto';
import { PostOrderResponseDto } from './dtos/PostOrderResponseDto';
import { PostTransferOrderDto } from './dtos/PostTransferOrderDto';
import { PostWithdrawalOrderDto } from './dtos/PostWithdrawalOrderDto';
import { OrderWithSignUrl } from './models/OrderWithSignUrl';
import { PendingOrder } from './models/PendingOrder';
import { PostOrderResponse } from './models/PostOrderResponse';
import { mapOrderWithSignUrlDtoToModel } from './utils/mapOrderWithSignUrlDtoToModel';
import { transformPendingOrdersReply } from './utils/transformPendingOrdersReply';

export const orderApi = createApi({
  reducerPath: 'orderApi',
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    getOrderById: builder.query<OrderWithSignUrl, { orderId: string }>({
      query: ({ orderId }) => {
        return {
          url: `${window.config.connectBaseApiUrl}/api/orders/order/${orderId}`,
        };
      },
      transformResponse: mapOrderWithSignUrlDtoToModel,
    }),

    pendingOrders: builder.query<PendingOrder[], void>({
      query: () => ({
        url: `${window.config.connectBaseApiUrl}/api/orders/v2/pending`,
      }),
      transformResponse: (reply: PendingOrdersReply) => {
        return transformPendingOrdersReply(reply);
      },
    }),

    postOrder: builder.mutation<
      PostOrderResponse,
      {
        orderType: OrderType;
        order:
          | PostOrderDto
          | PostDepositOrderDto
          | PostWithdrawalOrderDto
          | PostTransferOrderDto;
      }
    >({
      query: ({ orderType, order }) => {
        const urlMap = {
          [OrderType.Subscription]: `${window.config.connectBaseApiUrl}/api/orders/v3/buy/fund`,
          [OrderType.Redemption]: `${window.config.connectBaseApiUrl}/api/orders/v3/sell/fund`,
          [OrderType.Switch]: `${window.config.connectBaseApiUrl}/api/orders/v3/switch/fund`,
          [OrderType.Deposit]: `${window.config.connectBaseApiUrl}/api/orders/v3/deposit/discretionary`,
          [OrderType.Withdrawal]: `${window.config.connectBaseApiUrl}/api/orders/v3/withdraw/discretionary`,
          [OrderType.Transfer]: `${window.config.connectBaseApiUrl}/api/orders/v3/transfer/discretionary`,
        };

        return {
          url: urlMap[orderType],
          method: 'post',
          data: order,
        };
      },
      transformResponse: function (dto: PostOrderResponseDto) {
        return {
          orderId: dto.order.id,
          signUrl: dto.signUrl,
        };
      },
    }),

    /**
     * Mark the order as signed by the logged in user.
     *
     * We don't know who or when signatories has signed the order until ESS sends an APEX message that
     * all parties have signed the order. Thus, to track when individual users signs the order, we
     * do a call to update the signing order status for the current user.
     */
    confirmOrderSigned: builder.mutation<void, { orderId: string }>({
      query: ({ orderId }) => ({
        url: `${window.config.connectBaseApiUrl}/api/orders/signed/${orderId}`,
        method: 'put',
      }),
    }),
  }),
});

export const {
  useGetOrderByIdQuery,
  usePendingOrdersQuery,
  usePostOrderMutation,
  useConfirmOrderSignedMutation,
} = orderApi;
